import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Github, Linkedin, X, Building2, Award, Users, Target } from 'lucide-react';
import Footer from '../components/Footer';
import { RAMSEY_ENABLED } from '../config/features';
import Breadcrumb from '../components/Breadcrumb';
import { ChapterCard } from '../components/story';

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "name": "Dopove",
      "url": "https://dopove.com",
      "logo": "https://dopove.com/logo_new_16x9_trans_inverted.png",
      "sameAs": [
        "https://x.com/dopove",
        "https://peerlist.io/dopove",
        "https://github.com/dopove",
        "https://linkedin.com/company/dopove"
      ],
      "foundingDate": "2024",
      "description": "Dopove builds useful memory for AI agents and applies systems thinking to how it works. Its first product is ICE, the Indexless Context Engine.",
      "foundingLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "US"
        }
      },
      "employee": {
        "@type": "Person",
        "name": "Saran",
        "jobTitle": "Founder & CEO",
        "url": "https://peerlist.io/saran"
      }
    },
    {
      "@type": "AboutPage",
      "name": "About Dopove",
      "description": "Dopove is a systems thinking company building useful memory for AI agents. Meet the team, see how we work, and read what we are learning.",
      "url": "https://dopove.com/about",
      "mainEntity": {
        "@type": "Organization",
        "name": "Dopove"
      }
    },
    {
      "@type": "Person",
      "name": "Saran",
      "jobTitle": "Founder & CEO",
      "url": "https://peerlist.io/saran",
      "worksFor": {
        "@type": "Organization",
        "name": "Dopove"
      },
      "description": "Builds ICE, and applies systems thinking to both product and company.",
      "sameAs": [
        "https://x.com/saran",
        "https://peerlist.io/saran",
        "https://github.com/saran"
      ]
    }
  ]
};

const ValueCard = ({ icon: Icon, title, description, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.8, delay: index * 0.1, ease: [0.23, 1, 0.32, 1] }}
    className="bg-[#23263a]/40 p-8 rounded-[2rem] border border-[#FFFFFF]/10 hover-lift focus-visible-ring transition-[transform,border-color,background-color,box-shadow] duration-200 ease-[var(--ease-out)] backdrop-blur-sm"
  >
    <div className="bg-[#191d2b] p-4 rounded-xl mb-6 inline-flex">
      <Icon className="h-7 w-7 text-[#6FB6E8]" />
    </div>
    <h3 className="text-xl font-bold mb-3 text-[#FFFFFF]">{title}</h3>
    <p className="text-[#FFFFFF]/70 leading-relaxed font-light">{description}</p>
  </motion.div>
);

const TeamCard = ({ name, title, bio, social, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.8, delay: index * 0.15, ease: [0.23, 1, 0.32, 1] }}
    className="bg-[#23263a]/40 p-8 rounded-[2rem] border border-[#FFFFFF]/10 text-center hover-lift focus-visible-ring transition-[transform,border-color,background-color,box-shadow] duration-200 ease-[var(--ease-out)] backdrop-blur-sm"
  >
    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#6FB6E8]/20 to-[#1E7FD1]/20 border border-[#6FB6E8]/30 flex items-center justify-center">
      <span className="text-3xl font-bold text-[#6FB6E8]">{name.charAt(0)}</span>
    </div>
    <h3 className="text-xl font-bold text-[#FFFFFF] mb-1">{name}</h3>
    <p className="text-[#6FB6E8] text-sm font-medium mb-4">{title}</p>
    <p className="text-[#FFFFFF]/70 text-sm leading-relaxed mb-6">{bio}</p>
    <div className="flex justify-center gap-4">
      {social.map((s, i) => (
        <a
          key={i}
          href={s.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#FFFFFF]/50 hover:text-[#6FB6E8] transition-colors"
          aria-label={s.label}
        >
          <s.icon className="w-5 h-5" />
        </a>
      ))}
    </div>
  </motion.div>
);

const MilestoneCard = ({ year, title, description, chapter, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -30 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.8, delay: index * 0.1, ease: [0.23, 1, 0.32, 1] }}
    className="relative pl-8 pb-12 border-l border-[#FFFFFF]/10 last:border-0 last:pb-0"
  >
    <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-[#1E7FD1] border-4 border-[#191d2b] flex items-center justify-center z-10">
      <span className="text-xs font-bold text-white">{year}</span>
    </div>
    {chapter && (
      <span className="text-[10px] font-mono tracking-widest text-[#6FB6E8] uppercase mb-2 block" style={{ opacity: 0.8 }}>
        {chapter}
      </span>
    )}
    <h3 className="text-lg font-bold text-[#FFFFFF] mb-2">{title}</h3>
    <p className="text-[#FFFFFF]/60 text-sm">{description}</p>
  </motion.div>
);

export default function AboutPage() {
  const team = [
    {
      name: "Saran",
      title: "Founder & CEO",
      bio: "Builds ICE, and applies systems thinking to both product and company.",
      social: [
        { icon: X, url: "https://x.com/saran", label: "X" },
        { icon: Linkedin, url: "https://linkedin.com/in/saran", label: "LinkedIn" },
        { icon: Github, url: "https://github.com/saran", label: "GitHub" }
      ]
    }
  ];

  const milestones = [
    { year: "2024", title: "Dopove Founded", description: "Dopove Private Limited founded in Chennai, India.", chapter: "Ch. 0" },
    { year: "2024", title: "ICE v1 Released", description: "Our first product, the Indexless Context Engine.", chapter: "Ch. 1" },
    { year: "2024", title: "NVIDIA Inception", description: "Accepted into NVIDIA Inception program for AI startups", chapter: "Ch. 0" },
    { year: "2025", title: "Cortex Open Source", description: "High-performance multi-agent runtime released on GitHub", chapter: "Ch. 4" },
    { year: "2025", title: "ICE Nexus Beta", description: "Cross-agent memory layer for vibe coding workflows", chapter: "Ch. 1" }
  ];

  return (
    <div className="bg-[#191d2b] min-h-screen text-[#FFFFFF] flex flex-col font-sans selection:bg-[#6FB6E8]/30 overflow-x-hidden">
      <Helmet>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
        <title>About Dopove: systems thinking for AI agent memory</title>
        <meta name="description" content="Dopove is a systems thinking company building useful memory for AI agents. Meet the team, see how we work, and read what we are learning." />
        <meta property="og:title" content="About Dopove: systems thinking for AI agent memory" />
        <meta property="og:description" content="Dopove is a systems thinking company building useful memory for AI agents. Meet the team, see how we work, and read what we are learning." />
        <meta property="og:image" content="https://dopove.com/logo_new_16x9_trans_inverted.png" />
        <meta property="og:url" content="https://dopove.com/about" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Dopove: systems thinking for AI agent memory" />
        <meta name="twitter:description" content="Dopove is a systems thinking company building useful memory for AI agents. Meet the team, see how we work, and read what we are learning." />
        <meta name="twitter:image" content="https://dopove.com/logo_new_16x9_trans_inverted.png" />
        <link rel="canonical" href="https://dopove.com/about" />
      </Helmet>

      
      <main className="flex-grow">
        {/* Hero Section */}
        <section
          id="hero"
          aria-labelledby="hero-heading"
          className="relative min-h-[80vh] flex flex-col items-center justify-center px-6 pt-32 pb-20"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(111,182,232,0.1)_0%,transparent_50%)]" />

          <div className="max-w-4xl mx-auto w-full relative z-10 mb-4">
            <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'About' }]} />
          </div>

          <div className="max-w-4xl mx-auto w-full text-center relative z-10">
            <motion.h1
              id="hero-heading"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.08] text-balance text-[#FFFFFF] mb-8"
            >
              Systems thinking, applied to <span className="text-[#6FB6E8]">the product</span> and to the company.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="text-lg sm:text-xl md:text-2xl text-[#FFFFFF]/70 font-light leading-relaxed max-w-2xl mx-auto mb-12"
            >
              Dopove is a systems thinking company. We build useful memory for AI agents, and we apply the same thinking to how we run our own marketing.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link to="/ice" className="px-8 py-4 rounded-full bg-[#1E7FD1] text-white font-bold shadow-lg hover-scale-press focus-visible-ring transition-[transform,box-shadow] duration-200 ease-[var(--ease-out)]">
                Explore ICE
              </Link>
              <Link to="/goingmerry" className="px-8 py-4 rounded-full border border-[#62B7FF]/30 text-white font-bold hover:bg-[#62B7FF]/5 hover-scale-press focus-visible-ring transition-[background-color,border-color,transform] duration-200 ease-[var(--ease-out)]">
                Explore GoingMerry
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section
          id="mission"
          aria-labelledby="mission-heading"
          className="bg-[#191d2b] py-24 border-y border-[#FFFFFF]/5"
        >
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  className="mb-8"
                >
                  <h2 id="mission-heading" className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight text-[#FFFFFF]">
                    Systems thinking, applied twice.
                  </h2>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ delay: 0.1 }}
                  className="space-y-8"
                >
                  <p className="text-[#FFFFFF]/70 text-lg font-light leading-relaxed">
                    Dopove was founded by a technical builder who applies systems thinking to both product design and company operations, rather than separating technical excellence from marketing chaos.
                  </p>
                  <p className="text-[#FFFFFF]/70 text-lg font-light leading-relaxed">
                    Our current exploration is applying systems thinking to reduce founder marketing workload through an autonomous marketing approach. We build in public: we share what works, what does not, and what we are learning, about agent memory and about running a better company.
                  </p>
                </motion.div>
              </div>

              <div className="relative">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="bg-gradient-to-br from-[#23263a]/60 to-[#191d2b] rounded-[3rem] p-12 border border-[#FFFFFF]/10 shadow-2xl relative z-10"
                >
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 p-6 bg-[#191d2b] rounded-2xl border border-[#FFFFFF]/5">
                      <div className="p-3 rounded-xl bg-[#6FB6E8]/10 border border-[#6FB6E8]/20">
                        <Target className="h-6 w-6 text-[#6FB6E8]" />
                      </div>
                      <div>
                        <h4 className="font-bold text-white mb-1">Memory for agents</h4>
                        <p className="text-white/60 text-sm">ICE keeps the order of events, retrieves by lane, and carries memory across sessions</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-6 bg-[#191d2b] rounded-2xl border border-[#FFFFFF]/5">
                      <div className="p-3 rounded-xl bg-[#62B7FF]/10 border border-[#62B7FF]/20">
                        <Users className="h-6 w-6 text-[#62B7FF]" />
                      </div>
                      <div>
                        <h4 className="font-bold text-white mb-1">Local runtime</h4>
                        <p className="text-white/60 text-sm">GoingMerry, our next product, runs models on your own hardware</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-6 bg-[#191d2b] rounded-2xl border border-[#FFFFFF]/5">
                      <div className="p-3 rounded-xl bg-[#e1dabe]/10 border border-[#e1dabe]/20">
                        <Building2 className="h-6 w-6 text-[#e1dabe]" />
                      </div>
                      <div>
                        <h4 className="font-bold text-white mb-1">Agent orchestration</h4>
                        <p className="text-white/60 text-sm">Cortex is an open-source runtime for multi-agent systems</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section
          id="values"
          aria-labelledby="values-heading"
          className="py-24"
        >
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-[#6FB6E8] font-mono font-bold tracking-[0.4em] uppercase text-xs mb-4 block">Principles</span>
                <h2 id="values-heading" className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight text-[#FFFFFF]">How We Build</h2>
                <p className="text-[#FFFFFF]/50 text-lg font-light max-w-2xl mx-auto">Four principles that guide every architectural decision</p>
              </motion.div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: Target,
                  title: "Precision Over Features",
                  description: "We ship fewer things better. Every API and every CLI command is intentional."
                },
                {
                  icon: Users,
                  title: "Developer Experience First",
                  description: "If it takes more than 3 lines to integrate, we failed. The best infrastructure is invisible."
                },
                {
                  icon: Building2,
                  title: "Production from Day One",
                  description: "No toy prototypes. Type-safe, auditable, monitored, and scalable from the first commit."
                },
                {
                  icon: Award,
                  title: "Open by Default",
                  description: "Cortex is open source on GitHub and the ICE Community Edition is free to run. Community drives adoption; enterprise drives sustainability."
                }
              ].map((v, i) => (
                <ValueCard key={v.title} icon={v.icon} title={v.title} description={v.description} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* Products Portfolio — The Cast */}
        <section
          id="products"
          aria-labelledby="products-heading"
          className="bg-[#191d2b] py-24 border-y border-[#FFFFFF]/5"
        >
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="text-[#6FB6E8] font-mono font-bold tracking-[0.4em] uppercase text-xs mb-4 block">The Cast</span>
              <h2 id="products-heading" className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight text-[#FFFFFF]">Three products, one way of thinking.</h2>
              <p className="text-[#FFFFFF]/50 text-lg font-light max-w-2xl mx-auto">GoingMerry is the next product we are building after ICE.</p>
            </div>

            <div className={RAMSEY_ENABLED ? "grid md:grid-cols-4 gap-8" : "grid md:grid-cols-3 gap-8"}>
              <ChapterCard
                chapter={1}
                theme="Memory"
                name="ICE"
                color="ice"
                tagline="Indexless Context Engine. Turn-granular memory for AI agents."
                role="Our first product"
                href="/ice"
                index={0}
              />
              <ChapterCard
                chapter={2}
                theme="Velocity"
                name="GoingMerry"
                color="goingmerry"
                tagline="Local AI runtime. Run models privately on your own hardware."
                role="The next product after ICE"
                href="/goingmerry"
                index={1}
              />
              {RAMSEY_ENABLED && (
                <ChapterCard
                  chapter={3}
                  theme="Truth"
                  name="Ramsey"
                  color="ramsey"
                  tagline="Deterministic reasoning engine. Logic without hallucination."
                  role="The judge — formal verification for critical paths"
                  href="/ramsey"
                  index={2}
                />
              )}
              <ChapterCard
                chapter={RAMSEY_ENABLED ? 4 : 3}
                theme="Symphony"
                name="Cortex"
                color="cortex"
                tagline="Multi-agent orchestration runtime. Auditable, not just functional."
                role="Open source, on GitHub"
                href="https://github.com/Dopove/Cortex"
                external={true}
                index={RAMSEY_ENABLED ? 3 : 2}
              />
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section
          id="history"
          aria-labelledby="history-heading"
          className="py-24"
        >
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="text-[#6FB6E8] font-mono font-bold tracking-[0.4em] uppercase text-xs mb-4 block">Timeline</span>
              <h2 id="history-heading" className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight text-[#FFFFFF]">Our Journey</h2>
              <p className="text-[#FFFFFF]/50 text-lg font-light max-w-2xl mx-auto">From founding to production-grade infrastructure</p>
            </div>

            <div className="max-w-3xl mx-auto">
              {milestones.map((m, i) => (
                <MilestoneCard key={`${m.year}-${m.title}`} year={m.year} title={m.title} description={m.description} chapter={m.chapter} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section
          id="team"
          aria-labelledby="team-heading"
          className="bg-[#191d2b] py-24 border-y border-[#FFFFFF]/5"
        >
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="text-[#6FB6E8] font-mono font-bold tracking-[0.4em] uppercase text-xs mb-4 block">Leadership</span>
              <h2 id="team-heading" className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight text-[#FFFFFF]">The Team</h2>
              <p className="text-[#FFFFFF]/50 text-lg font-light max-w-2xl mx-auto">Small team, outsized ambition</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {team.map((t, i) => (
                <TeamCard key={t.name} {...t} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* NVIDIA Inception */}
        <section
          id="inception"
          aria-labelledby="inception-heading"
          className="py-24"
        >
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              className="mb-10 flex justify-center"
            >
              <img
                src="/nvidia-inception-program-badge-rgb-for-screen.png"
                alt="NVIDIA Inception Program Member"
                className="h-24 md:h-32 w-auto object-contain drop-shadow-[0_0_20px_rgba(118,185,0,0.2)]"
              />
            </motion.div>
            <h2 id="inception-heading" className="text-3xl md:text-4xl font-bold mb-6 text-[#FFFFFF]">NVIDIA Inception Member</h2>
            <p className="text-xl text-[#FFFFFF]/70 font-light leading-relaxed max-w-2xl mx-auto mb-10">
              Dopove is a member of the <span className="font-semibold text-[#76B900]">NVIDIA Inception program</span>, which nurtures startups transforming industries with advancements in AI and data sciences.
            </p>
            <p className="text-[10px] text-[#FFFFFF]/30 font-mono tracking-wider max-w-2xl mx-auto uppercase">
              © 2025 NVIDIA, the NVIDIA logo, and NVIDIA Inception are trademarks and/or registered trademarks of NVIDIA Corporation in the U.S. and other countries.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section
          id="cta"
          aria-labelledby="cta-heading"
          className="bg-[#191d2b] py-32 border-t border-[#FFFFFF]/5"
        >
          <div className="max-w-4xl mx-auto px-6 text-center space-y-8">
            <h2 id="cta-heading" className="text-4xl md:text-6xl font-extrabold tracking-tight text-[#FFFFFF]">
              Join Us in Building the Future
            </h2>
            <p className="text-[#FFFFFF]/60 text-xl font-light leading-relaxed max-w-2xl mx-auto">
              We're looking for engineers who care about memory architecture, compiler optimization,
              and making AI infrastructure disappear.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="mailto:info@dopove.com" className="px-8 py-4 rounded-full bg-[#1E7FD1] text-white font-bold shadow-lg hover-scale-press focus-visible-ring transition-[transform,box-shadow] duration-200 ease-[var(--ease-out)]">
                Get in Touch
              </a>
              <Link to="/ice" className="px-8 py-4 rounded-full border border-[#1E7FD1]/20 text-[#FFFFFF] font-bold hover:bg-[#6FB6E8]/5 hover-scale-press focus-visible-ring transition-[background-color,transform] duration-200 ease-[var(--ease-out)]">
                Explore Our Tech
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}